import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPage } from '../lib/cms';
import ContactForm from '../components/ContactForm';
import { useScrollReveal } from '../hooks/useScrollReveal';

const services = [
  {
    title: 'Asfalt',
    slug: 'asfalt',
    icon: 'fa-solid fa-road',
    desc: 'Slidstærke og jævne asfaltløsninger til indkørsler, veje og stier på hele Sydfyn.',
    image: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/asfalt-arbejde.jpg'
  },
  {
    title: 'Rørlægning',
    slug: 'roerlaegning',
    icon: 'fa-solid fa-trowel-bricks',
    desc: 'Professionel håndtering af spildevand, regnvand og drænløsninger på Sydfyn.',
    image: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/roerlaegning.jpg'
  },
  {
    title: 'Entreprenørarbejde',
    slug: 'entreprenoerarbejde',
    icon: 'fa-solid fa-trench-shovel',
    desc: 'Jordarbejde, udgravning og terrænregulering udført med moderne maskiner.',
    image: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/entreprenoerarbejde.jpg'
  },
  {
    title: 'Mandskabsudlejning',
    slug: 'mandskabsudlejning',
    icon: 'fa-solid fa-users-gear',
    desc: 'Erfarne og selvkørende folk til entreprenør-, asfalt- og anlægsprojekter.',
    image: 'https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/mandskabsudlejning.jpg'
  }
];

const processSteps = [
  {
    num: '01',
    title: 'Dialog',
    desc: 'Vi starter altid med en grundig snak om dine ønsker og behov, så vi sikrer fuld forventningsafstemning.'
  },
  {
    num: '02',
    title: 'Planlægning',
    desc: 'Vi udarbejder en klar plan og tidsramme for arbejdet, så du ved præcis, hvad der skal ske og hvornår.'
  },
  {
    num: '03',
    title: 'Udførelse',
    desc: 'Vores erfarne mandskab udfører arbejdet med stor præcision og moderne maskiner efter gældende standarder.'
  },
  {
    num: '04',
    title: 'Aflevering',
    desc: 'Vi gennemgår det færdige resultat sammen med dig, så vi er 100% sikre på, at du er fuldt ud tilfreds.'
  }
];

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const [introRef, introRevealed] = useScrollReveal();
  const [servicesRef, servicesRevealed] = useScrollReveal();
  const [coverageRef, coverageRevealed] = useScrollReveal();
  const [processRef, processRevealed] = useScrollReveal();

  useEffect(() => {
    async function loadData() {
      const data = await fetchPage('page-forside');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title || 'JE Entreprenør';
  const metaDesc = pageData?.meta_description || 'JE Entreprenør udfører asfalt, rørlægning, entreprenørarbejde og mandskabsudlejning på Sydfyn.';
  const heroTitle = pageData?.hero_title || 'Entreprenørarbejde på Sydfyn – solidt udført fra start til slut';
  const heroSubtitle = pageData?.hero_subtitle || 'JE Entreprenør hjælper private, erhverv og samarbejdspartnere med asfalt, rørlægning, entreprenørarbejde og mandskabsudlejning på hele Sydfyn.';

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/hero-entreprenoer.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[65vh] flex items-center justify-center bg-asphalt-950 text-white overflow-hidden pt-24 sm:pt-28 pb-16 sm:pb-20">
        {/* Background Image with Cloudflare Resizing */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/hero-entreprenoer.jpg"
            alt="JE Entreprenør byggeplads"
            className="w-full h-full object-cover opacity-55 scale-[1.3] transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-asphalt-950/75 via-asphalt-950/35 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text & Direct Call */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-black uppercase tracking-tight text-white leading-tight">
                {heroTitle}
              </h1>
              
              <p className="mt-6 text-base sm:text-lg text-white leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {heroSubtitle}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6">
                <a
                  href="tel:+4551172145"
                  className="inline-flex items-center justify-center text-lg sm:text-xl font-bold text-white hover:text-safety-orange hover:-translate-y-1 transition-all duration-200"
                >
                  <i className="fa-solid fa-phone mr-3 text-safety-orange"></i>
                  51 17 21 45
                </a>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-5 w-full max-w-lg mx-auto lg:max-w-none text-left animate-shake">
              <ContactForm idPrefix="hero_" />
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section 
        ref={introRef}
        className={`py-16 bg-white border-b border-asphalt-100 reveal-fade-up ${introRevealed ? 'is-revealed' : ''}`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            {/* Image Column (Now Left) */}
            <div className="relative order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/entreprenoer-galleri-1.jpg"
                alt="JE Entreprenør gravemaskine i arbejde"
                className="rounded-2xl shadow-xl object-cover w-full h-[400px]"
              />
              <div className="absolute -bottom-6 -right-6 hidden sm:block bg-white p-6 text-asphalt-900 shadow-lg max-w-xs border border-black">
                <p className="font-display text-3xl font-black text-safety-orange">100%</p>
                <p className="mt-1 text-sm font-bold uppercase tracking-wider">Lokalt forankret på Sydfyn</p>
              </div>
            </div>

            {/* Text Column (Now Right) */}
            <div className="order-1 lg:order-2">
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-4xl">
                Kvalitet og pålidelighed i højsædet
              </h2>
              <div 
                className="mt-6 text-base text-asphalt-600 space-y-4 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: pageData?.content || '<p>Indlæser...</p>' }}
              />
              <div className="mt-8 flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-olive-600 text-lg"></i>
                  <span className="text-sm font-bold text-asphalt-800">Erfarent mandskab</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-olive-600 text-lg"></i>
                  <span className="text-sm font-bold text-asphalt-800">Moderne maskinpark</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        ref={servicesRef}
        className={`py-24 bg-sand-100 reveal-fade-up ${servicesRevealed ? 'is-revealed' : ''}`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-4xl">
              Vores Primære Ydelser
            </h2>
            <p className="mt-4 text-base text-asphalt-600 max-w-2xl">
              Vi løser en bred vifte af opgaver for både private, erhverv og offentlige instanser på hele Sydfyn.
            </p>
          </div>

          {/* Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Left Column: Interactive List Items */}
            <div className="flex flex-col gap-4 justify-between">
              {services.map((service, index) => {
                const isActive = activeIndex === index;
                return (
                  <Link
                    key={service.slug}
                    to={`/ydelser/${service.slug}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => setActiveIndex(index)}
                    className={`group block p-5 sm:p-8 rounded-2xl transition-all duration-500 text-left ${
                      isActive
                        ? 'bg-asphalt-900 text-white shadow-lg'
                        : 'bg-white text-asphalt-900 hover:bg-asphalt-50'
                    }`}
                  >
                    {/* Top Row: Large, right-aligned primary heading */}
                    <div className="flex justify-end">
                      <h3 className={`font-display text-lg min-[380px]:text-xl sm:text-3xl font-medium uppercase tracking-wider break-words text-right transition-colors duration-300 ${
                        isActive ? 'text-white' : 'text-asphalt-900 group-hover:text-safety-orange'
                      }`}>
                        {service.title}
                      </h3>
                    </div>

                    {/* Bottom Row: Smaller left-aligned description and right-aligned directional arrow icon */}
                    <div className="mt-6 flex justify-between items-end gap-6">
                      <p className={`text-xs sm:text-sm font-light tracking-wider leading-relaxed max-w-md transition-colors duration-300 ${
                        isActive ? 'text-asphalt-300' : 'text-asphalt-600'
                      }`}>
                        {service.desc}
                      </p>
                      <i className="fa-solid fa-arrow-right text-lg text-safety-orange transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0 mb-1"></i>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Right Column: Full-bleed Image Container */}
            <div className="relative min-h-[400px] lg:min-h-full rounded-2xl overflow-hidden shadow-xl">
              {services.map((service, index) => (
                <div
                  key={service.slug}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    activeIndex === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/${service.image.replace('https://assets.auxodev.dk/', '')}`}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  {/* Subtle overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-asphalt-950/40 via-transparent to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Local Coverage Section */}
      <section 
        ref={coverageRef}
        className={`py-20 bg-asphalt-900 text-white relative overflow-hidden reveal-fade-up ${coverageRevealed ? 'is-revealed' : ''}`}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(#ff6b00_1px,transparent_1px)] [background-size:16px_16px]" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div>
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                Vi kører på hele Sydfyn
              </h2>
              <p className="mt-4 text-base text-white leading-relaxed">
                Med base i Skårup Fyn er vi strategisk placeret til hurtigt at kunne rykke ud til opgaver på hele Sydfyn. Vi kender lokalområdet og dækker blandt andet:
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-location-arrow text-safety-orange"></i>
                  <span className="text-sm font-semibold text-asphalt-100">Svendborg</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-location-arrow text-safety-orange"></i>
                  <span className="text-sm font-semibold text-asphalt-100">Skårup & Nyborg</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-location-arrow text-safety-orange"></i>
                  <span className="text-sm font-semibold text-asphalt-100">Faaborg-Midtfyn</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-location-arrow text-safety-orange"></i>
                  <span className="text-sm font-semibold text-asphalt-100">Langeland & Tåsinge</span>
                </div>
              </div>
            </div>
            <div className="border border-asphalt-200 bg-asphalt-50 p-8 text-asphalt-900">
              <h3 className="font-display text-xl font-bold text-asphalt-900">Hvorfor vælge os?</h3>
              <ul className="mt-6 space-y-4">
                <li className="flex gap-3 items-start">
                  <i className="fa-solid fa-check text-safety-orange mt-1"></i>
                  <div>
                    <h4 className="text-sm font-bold text-asphalt-900">Lokalt kendskab</h4>
                    <p className="mt-1 text-xs text-asphalt-600">Vi bor og arbejder på Sydfyn, hvilket sikrer hurtig udrykning og personlig service.</p>
                  </div>
                </li>
                <li className="flex gap-3 items-start">
                  <i className="fa-solid fa-check text-safety-orange mt-1"></i>
                  <div>
                    <h4 className="text-sm font-bold text-asphalt-900">Gennemsigtige priser</h4>
                    <p className="mt-1 text-xs text-asphalt-600">Du får altid et skriftligt, uforpligtende tilbud med faste aftaler og ingen skjulte gebyrer.</p>
                  </div>
                </li>
                <li className="flex gap-3 items-start">
                  <i className="fa-solid fa-check text-safety-orange mt-1"></i>
                  <div>
                    <h4 className="text-sm font-bold text-asphalt-900">Høj faglig stolthed</h4>
                    <p className="mt-1 text-xs text-asphalt-600">Vi afleverer ikke et projekt, før det lever op til vores egne høje kvalitetskrav.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Work Process Section */}
      <section 
        ref={processRef}
        className={`py-20 bg-white border-b border-asphalt-100 reveal-fade-up ${processRevealed ? 'is-revealed' : ''}`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-4xl">
              Hvordan vi arbejder
            </h2>
            <p className="mt-4 text-base text-asphalt-600">
              Vi sikrer en tryg og gennemskuelig proces fra den første kontakt til den endelige aflevering af projektet.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-black">
            {processSteps.map((step, index) => (
              <div 
                key={step.num} 
                className={`relative flex flex-col p-8 bg-transparent border-black
                  border-b last:border-b-0
                  ${index < 2 ? 'md:border-b' : 'md:border-b-0'}
                  ${index % 2 === 0 ? 'md:border-r' : 'md:border-r-0'}
                  lg:border-b-0
                  ${index < 3 ? 'lg:border-r' : 'lg:border-r-0'}
                `}
              >
                <div className="font-display text-4xl font-black text-asphalt-950">{step.num}</div>
                <h3 className="mt-4 font-display text-lg font-bold text-asphalt-900">{step.title}</h3>
                <p className="mt-2 text-sm text-asphalt-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
